import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  GripVertical,
  Plus,
  Save,
  RefreshCw,
  Trash2,
  Edit,
  ChevronRight,
  ChevronDown,
  FolderOpen,
  FileText,
  Eye,
  EyeOff,
  X,
  Copy,
  FileDown,
  ExternalLink,
  Check,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Separator } from "./ui/separator";
import { ScrollArea } from "./ui/scroll-area";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "./ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { iconMap } from "../lib/iconRegistry";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GUARD_OPTIONS = [
  "protected",
  "admin",
  "module",
];

const ICON_NAMES = Object.keys(iconMap).sort();

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function flattenTree(items, depth = 0) {
  const flat = [];
  for (const item of items) {
    const isGroup = !!item.children;
    flat.push({ ...item, _depth: depth, _isGroup: isGroup });
    if (item.children) {
      flat.push(...flattenTree(item.children, depth + 1));
    }
  }
  return flat;
}

function buildTree(flat) {
  const root = [];
  const stack = [{ children: root, depth: -1 }];
  for (const item of flat) {
    const { _depth, _isGroup, ...rest } = item;
    while (stack.length > 1 && stack[stack.length - 1].depth >= _depth) {
      stack.pop();
    }
    const node = _isGroup ? { ...rest, children: [] } : { ...rest };
    delete node.children;
    if (_isGroup) node.children = [];
    stack[stack.length - 1].children.push(node);
    if (_isGroup) {
      stack.push({ children: node.children, depth: _depth });
    }
  }
  return root;
}

function generateId() {
  return "item_" + Date.now() + "_" + Math.random().toString(36).slice(2, 6);
}

function findItem(items, id) {
  for (let i = 0; i < items.length; i++) {
    if (items[i].id === id) return items[i];
    if (items[i].children) {
      const found = findItem(items[i].children, id);
      if (found) return found;
    }
  }
  return null;
}

function removeItem(items, id) {
  for (let i = 0; i < items.length; i++) {
    if (items[i].id === id) {
      items.splice(i, 1);
      return true;
    }
    if (items[i].children) {
      if (removeItem(items[i].children, id)) return true;
    }
  }
  return false;
}

const EMPTY_ITEM = {
  id: "",
  label: "",
  icon: "",
  path: "",
  component: "",
  guard: "protected",
  moduleId: "",
  tabId: "",
  visibleInMenu: true,
};

function makeEmptyItem(isGroup = false) {
  return {
    ...EMPTY_ITEM,
    id: generateId(),
    ...(isGroup ? { children: [] } : {}),
  };
}

function validateItem(item) {
  const errors = [];
  if (!item.id) errors.push("ID é obrigatório");
  if (!item.label) errors.push("Label é obrigatório");
  if (!item.children && !item.path) errors.push("Path é obrigatório (itens sem filhos)");
  if (!item.children && !item.component) errors.push("Component é obrigatório (itens sem filhos)");
  return errors;
}

export default function MenuEditorPage() {
  const [items, setItems] = useState([]);
  const [originalItems, setOriginalItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [dragState, setDragState] = useState(null);
  const [showPreview, setShowPreview] = useState(true);
  const [componentNames, setComponentNames] = useState([]);

  useEffect(() => {
    import("../lib/componentRegistry")
      .then((mod) => {
        if (mod?.componentMap)
          setComponentNames(Object.keys(mod.componentMap).sort());
      })
      .catch(() => {});
  }, []);

  const loadMenu = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/menu/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data;
      if (data && data.items) {
        setItems(data.items);
        setOriginalItems(deepClone(data.items));
      }
    } catch (err) {
      toast.error("Erro ao carregar configuração do menu");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMenu();
  }, [loadMenu]);

  const flatItems = useMemo(() => flattenTree(items), [items]);

  const hasChanges = useMemo(
    () => JSON.stringify(items) !== JSON.stringify(originalItems),
    [items, originalItems]
  );

  async function handleSave() {
    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      const payload = { items };
      await axios.put(`${API}/menu/config`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setOriginalItems(deepClone(items));
      toast.success("Menu salvo com sucesso!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar menu");
    } finally {
      setSaving(false);
    }
  }

  async function handleInit() {
    const hasItems = items.length > 0;
    const message = hasItems
      ? "Já existe uma configuração de menu personalizada. Deseja apagar e recriar a partir do MODULES_STRUCTURE? Itens personalizados serão perdidos."
      : "Isso irá gerar o menu a partir do MODULES_STRUCTURE. Continuar?";
    if (!window.confirm(message)) return;
    try {
      setInitLoading(true);
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/menu/config/init${hasItems ? "?force=true" : ""}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success(res.data.message || "Menu inicializado!");
      await loadMenu();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao inicializar menu");
    } finally {
      setInitLoading(false);
    }
  }

  function handleEdit(item) {
    setEditingItem(deepClone(item));
    setDialogOpen(true);
  }

  function handleAdd(isGroup = false) {
    const newItem = makeEmptyItem(isGroup);
    setEditingItem(newItem);
    setDialogOpen(true);
  }

  function handleAddChild(parentId) {
    const parent = findItem(items, parentId);
    if (!parent || !parent.children) {
      toast.error("Item não é um grupo");
      return;
    }
    const newItem = makeEmptyItem(false);
    setEditingItem({ ...newItem, _parentId: parentId });
    setDialogOpen(true);
  }

  function handleDelete(id) {
    const item = findItem(items, id);
    if (!item) return;
    const confirmMsg = item.children
      ? `Excluir grupo "${item.label}" e todos os seus ${item.children.length} filhos?`
      : `Excluir item "${item.label}"?`;
    if (!window.confirm(confirmMsg)) return;
    const newItems = deepClone(items);
    removeItem(newItems, id);
    setItems(newItems);
    toast.success("Item removido");
  }

  function handleDuplicate(id) {
    const item = findItem(items, id);
    if (!item) return;
    const newItems = deepClone(items);
    const original = findItem(newItems, id);
    if (!original) return;
    const dup = deepClone(original);
    dup.id = generateId();
    dup.label = dup.label + " (cópia)";
    const parent = findParent(newItems, id);
    if (parent) {
      parent.parent.splice(parent.index + 1, 0, dup);
    }
    setItems(newItems);
    toast.success("Item duplicado");
  }

  function findParent(tree, id) {
    for (let i = 0; i < tree.length; i++) {
      if (tree[i].id === id) return { parent: tree, index: i };
      if (tree[i].children) {
        const result = findParent(tree[i].children, id);
        if (result) return result;
      }
    }
    return null;
  }

  function handleDialogSave(formData) {
    const errors = validateItem(formData);
    if (errors.length > 0) {
      toast.error(errors.join(". "));
      return;
    }
    const newItems = deepClone(items);
    const { _parentId, ...itemData } = formData;
    if (!itemData.children) {
      delete itemData.children;
    }
    if (_parentId) {
      const parent = findItem(newItems, _parentId);
      if (parent && parent.children) {
        parent.children.push(itemData);
      }
    } else if (itemData.id && findItem(newItems, itemData.id)) {
      const parent = findParent(newItems, itemData.id);
      if (parent) {
        parent.parent[parent.index] = itemData;
      }
    } else {
      newItems.push(itemData);
    }
    setItems(newItems);
    setDialogOpen(false);
    setEditingItem(null);
    toast.success("Item salvo");
  }

  function handleDialogReplace(formData) {
    const errors = validateItem(formData);
    if (errors.length > 0) {
      toast.error(errors.join(". "));
      return;
    }
    const newItems = deepClone(items);
    const { _parentId, ...itemData } = formData;
    if (!itemData.children) {
      delete itemData.children;
    }
    const parent = findParent(newItems, itemData.id);
    if (parent) {
      parent.parent[parent.index] = itemData;
    }
    setItems(newItems);
    setDialogOpen(false);
    setEditingItem(null);
  }

  function toggleExpand(id) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleMoveUp(id) {
    const newItems = deepClone(items);
    const parent = findParent(newItems, id);
    if (parent && parent.index > 0) {
      const [removed] = parent.parent.splice(parent.index, 1);
      parent.parent.splice(parent.index - 1, 0, removed);
      setItems(newItems);
    }
  }

  function handleMoveDown(id) {
    const newItems = deepClone(items);
    const parent = findParent(newItems, id);
    if (parent && parent.index < parent.parent.length - 1) {
      const [removed] = parent.parent.splice(parent.index, 1);
      parent.parent.splice(parent.index + 1, 0, removed);
      setItems(newItems);
    }
  }

  function handleIndent(id) {
    const newItems = deepClone(items);
    const parent = findParent(newItems, id);
    if (parent && parent.index > 0) {
      const prevSibling = parent.parent[parent.index - 1];
      if (prevSibling && prevSibling.children) {
        const [removed] = parent.parent.splice(parent.index, 1);
        prevSibling.children.push(removed);
        setItems(newItems);
      }
    }
  }

  function handleOutdent(id) {
    const newItems = deepClone(items);
    const parent = findParent(newItems, id);
    if (parent) {
      const grandparent = findParent(newItems, parent.parent[0]?.id);
      if (!grandparent) return;
      const idx = grandparent.parent.findIndex(
        (p) => p.id === parent.parent[0]?.id
      );
      if (idx === -1) return;
      const [removed] = parent.parent.splice(parent.index, 1);
      grandparent.parent.splice(idx + 1, 0, removed);
      setItems(newItems);
    }
  }

  // ── Drag & Drop ──────────────────────────────────────────────
  function handleDragStart(e, id) {
    e.dataTransfer.setData("text/plain", id);
    e.dataTransfer.effectAllowed = "move";
    setDragState({ draggedId: id });
  }

  function handleDragOver(e, targetId, position) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragState((prev) =>
      prev && prev.draggedId !== targetId
        ? { ...prev, overId: targetId, position }
        : prev
    );
  }

  function handleDragLeave(e) {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragState((prev) => (prev ? { draggedId: prev.draggedId } : prev));
  }

  function handleDrop(e, targetId) {
    e.preventDefault();
    const draggedId = e.dataTransfer.getData("text/plain");
    if (draggedId === targetId) {
      setDragState(null);
      return;
    }

    const newItems = deepClone(items);

    const draggedParent = findParent(newItems, draggedId);
    if (!draggedParent) {
      setDragState(null);
      return;
    }
    const [draggedItem] = draggedParent.parent.splice(
      draggedParent.index,
      1
    );

    const target = findItem(newItems, targetId);
    if (!target) {
      newItems.push(draggedItem);
      setItems(newItems);
      setDragState(null);
      return;
    }

    const targetParent = findParent(newItems, targetId);
    if (!targetParent) {
      newItems.push(draggedItem);
      setItems(newItems);
      setDragState(null);
      return;
    }

    const pos = dragState?.position || "after";

    if (pos === "child" && target.children) {
      target.children.push(draggedItem);
    } else if (pos === "before") {
      targetParent.parent.splice(targetParent.index, 0, draggedItem);
    } else {
      targetParent.parent.splice(targetParent.index + 1, 0, draggedItem);
    }

    setItems(newItems);
    setDragState(null);
    toast.success("Item movido");
  }

  function handleDragEnd() {
    setDragState(null);
  }

  // ── Render helpers ──────────────────────────────────────────
  function renderIcon(name) {
    if (!name) return null;
    const Icon = iconMap[name];
    return Icon ? <Icon className="h-4 w-4" /> : null;
  }

  function renderGuardBadge(guard) {
    const colors = {
      protected: "bg-blue-100 text-blue-800",
      admin: "bg-red-100 text-red-800",
      module: "bg-purple-100 text-purple-800",
    };
    return guard ? (
      <Badge className={`text-xs ${colors[guard] || ""}`} variant="outline">
        {guard}
      </Badge>
    ) : null;
  }

  // ── RENDER ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Editor de Menu
          </h1>
          <p className="text-muted-foreground mt-1">
            Gerencie a estrutura do menu lateral. Arraste itens para
            reordenar, edite propriedades ou adicione novos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleInit}
            disabled={initLoading}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${initLoading ? "animate-spin" : ""}`}
            />
            {initLoading ? "Inicializando..." : "Init"}
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </div>

      <Separator />

      {/* Main content */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Tree Editor */}
        <div className="xl:col-span-2">
          <Card className="flex flex-col h-full">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">
                    Estrutura do Menu
                  </CardTitle>
                  <CardDescription>
                    {flatItems.length} itens
                    {hasChanges ? " (não salvo)" : ""}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAdd(true)}
                  >
                    <FolderOpen className="h-4 w-4 mr-2" />
                    Grupo
                  </Button>
                  <Button size="sm" onClick={() => handleAdd(false)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Item
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-1 min-h-0 p-0">
              <ScrollArea className="h-full">
                <TooltipProvider>
                <div className="p-4 space-y-1">
                  {flatItems.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                      <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p>Nenhum item de menu.</p>
                      <p className="text-sm">
                        Clique em "Init" para gerar do backend ou adicione
                        manualmente.
                      </p>
                    </div>
                  )}

                  {flatItems.map((item) => {
                    const isDragOver =
                      dragState?.overId === item.id;
                    const dropPosition = isDragOver
                      ? dragState?.position
                      : null;
                    const isDragging =
                      dragState?.draggedId === item.id;

                    return (
                      <React.Fragment key={item.id || item._depth + "_" + item.label}>
                        {/* Drop indicator — before */}
                        {isDragOver && dropPosition === "before" && (
                          <div className="flex items-center h-1 -mx-1">
                            <div className="h-0.5 w-full bg-primary rounded-full" />
                            <Check className="h-3 w-3 text-primary -ml-1" />
                          </div>
                        )}

                        {/* Tree row */}
                        <div
                          draggable
                          onDragStart={(e) => handleDragStart(e, item.id)}
                          onDragOver={(e) => {
                            if (item._isGroup) {
                              handleDragOver(e, item.id, "child");
                            } else {
                              handleDragOver(e, item.id, "after");
                            }
                          }}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, item.id)}
                          onDragEnd={handleDragEnd}
                          className={`
                            group relative flex items-center gap-2 px-2 py-2 rounded-lg border
                            transition-all duration-150 cursor-default
                            ${isDragging ? "opacity-30" : ""}
                            ${isDragOver && dropPosition === "child" ? "ring-2 ring-primary bg-primary/5" : ""}
                            ${isDragOver && dropPosition === "after" ? "border-b-primary" : ""}
                            hover:bg-accent/50
                          `}
                          style={{
                            marginLeft: `${item._depth * 20}px`,
                          }}
                        >
                          {/* Drag handle */}
                          <div className="cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground flex-shrink-0">
                            <GripVertical className="h-4 w-4" />
                          </div>

                          {/* Expand/Collapse for groups */}
                          {item._isGroup ? (
                            <button
                              onClick={() => toggleExpand(item.id)}
                              className="p-0.5 rounded hover:bg-accent flex-shrink-0"
                            >
                              {expandedIds.has(item.id) ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                          ) : (
                            <span className="w-5 flex-shrink-0" />
                          )}

                          {/* Icon */}
                          <span className="flex-shrink-0 w-5 flex items-center justify-center text-muted-foreground">
                            {item._isGroup ? (
                              <FolderOpen className="h-4 w-4" />
                            ) : (
                              renderIcon(item.icon) || (
                                <FileText className="h-4 w-4" />
                              )
                            )}
                          </span>

                          {/* Label */}
                          <span className="text-sm font-medium truncate flex-1 min-w-0">
                            {item.label || (
                              <span className="italic text-muted-foreground">
                                sem label
                              </span>
                            )}
                          </span>

                          {/* Badges */}
                          <div className="hidden md:flex items-center gap-1.5 flex-shrink-0">
                            {renderGuardBadge(item.guard)}
                            {item._isGroup && (
                              <Badge
                                variant="outline"
                                className="text-xs text-muted-foreground"
                              >
                                {item.children?.length || 0} itens
                              </Badge>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => handleEdit(item)}
                                  className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>Editar</TooltipContent>
                            </Tooltip>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => handleDuplicate(item.id)}
                                  className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
                                >
                                  <Copy className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>Duplicar</TooltipContent>
                            </Tooltip>

                            {item._isGroup && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => handleAddChild(item.id)}
                                    className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
                                  >
                                    <Plus className="h-3.5 w-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Adicionar filho
                                </TooltipContent>
                              </Tooltip>
                            )}

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => handleMoveUp(item.id)}
                                  className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
                                >
                                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M18 15l-6-6-6 6" />
                                  </svg>
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                Mover para cima
                              </TooltipContent>
                            </Tooltip>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => handleMoveDown(item.id)}
                                  className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
                                >
                                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M6 9l6 6 6-6" />
                                  </svg>
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                Mover para baixo
                              </TooltipContent>
                            </Tooltip>

                            <div className="w-px h-4 bg-border mx-0.5" />

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => handleDelete(item.id)}
                                  className="p-1 rounded hover:bg-red-100 text-muted-foreground hover:text-red-600"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="text-red-600">
                                Excluir
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </div>

                        {/* Drop indicator — after */}
                        {isDragOver && dropPosition === "after" && (
                          <div className="flex items-center h-1 -mx-1">
                            <div className="h-0.5 w-full bg-primary rounded-full" />
                            <Check className="h-3 w-3 text-primary -ml-1" />
                          </div>
                        )}

                        {/* Children of collapsed group */}
                        {item._isGroup &&
                          !expandedIds.has(item.id) &&
                          item.children?.length > 0 && (
                            <div
                              className="text-xs text-muted-foreground pl-10 pb-1"
                              style={{ marginLeft: `${item._depth * 20}px` }}
                            >
                              {item.children.length} item(ns) oculto(s)
                            </div>
                          )}
                      </React.Fragment>
                    );
                  })}
                </div>
                </TooltipProvider>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Preview Panel */}
        <div className="xl:col-span-1">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Pré-visualização</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                >
                  {showPreview ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <CardDescription>
                Como o menu aparecerá para os usuários
              </CardDescription>
            </CardHeader>
            {showPreview && (
              <CardContent>
                <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3 border space-y-0.5 text-sm">
                  <MenuPreviewTree items={items} depth={0} />
                  {items.length === 0 && (
                    <p className="text-muted-foreground text-center py-8">
                      Nenhum item para exibir
                    </p>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingItem?.id && findItem(items, editingItem.id)
                ? `Editar: ${editingItem.label || "sem label"}`
                : "Novo Item"}
            </DialogTitle>
            <DialogDescription>
              {editingItem?.children
                ? "Configurações do grupo de menu"
                : "Configurações do item de menu"}
            </DialogDescription>
          </DialogHeader>

          {editingItem && (
            <ItemForm
              item={editingItem}
              setItem={setEditingItem}
              componentNames={componentNames}
            />
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDialogOpen(false);
                setEditingItem(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() =>
                editingItem?.id && findItem(items, editingItem.id)
                  ? handleDialogReplace(editingItem)
                  : handleDialogSave(editingItem)
              }
            >
              <Check className="h-4 w-4 mr-2" />
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Item Form ──────────────────────────────────────────────────
function ItemForm({ item, setItem, componentNames }) {
  const isGroup = !!item.children;

  const update = (field, value) => {
    setItem((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-4 py-2">
      {/* ID */}
      <div className="space-y-1.5">
        <Label htmlFor="item-id">ID</Label>
        <Input
          id="item-id"
          value={item.id}
          onChange={(e) => update("id", e.target.value)}
          placeholder="identificador_unico"
        />
        <p className="text-xs text-muted-foreground">
          Identificador único usado no <code>permitted_ids</code> para controle de permissões. Apenas letras minúsculas, números e underscore.
        </p>
      </div>

      {/* Label */}
      <div className="space-y-1.5">
        <Label htmlFor="item-label">Label</Label>
        <Input
          id="item-label"
          value={item.label}
          onChange={(e) => update("label", e.target.value)}
          placeholder="Nome visível no menu"
        />
        <p className="text-xs text-muted-foreground">
          Texto exibido no menu lateral para este item.
        </p>
      </div>

      {/* Icon */}
      <div className="space-y-1.5">
        <Label htmlFor="item-icon">Ícone</Label>
        <Select value={item.icon} onValueChange={(v) => update("icon", v)}>
          <SelectTrigger id="item-icon">
            <SelectValue placeholder="Nenhum" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="(none)">Nenhum</SelectItem>
            {ICON_NAMES.map((name) => (
              <SelectItem key={name} value={name}>
                <span className="flex items-center gap-2">
                  {renderIconStatic(name)}
                  <span>{name}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Ícone exibido ao lado do label no menu. Selecione entre os ícones disponíveis da biblioteca Lucide.
        </p>
      </div>

      <Separator />

      {!isGroup && (
        <>
          {/* Path */}
          <div className="space-y-1.5">
            <Label htmlFor="item-path">Path</Label>
            <Input
              id="item-path"
              value={item.path || ""}
              onChange={(e) => update("path", e.target.value)}
              placeholder="/rota-do-item"
            />
            <p className="text-xs text-muted-foreground">
              Caminho da URL para esta rota. Ex: <code>/meu-modulo</code>, <code>/relatorios/financeiro</code>. Pode conter parâmetros como <code>:id</code> ou <code>:token</code>.
            </p>
          </div>

          {/* Component */}
          <div className="space-y-1.5">
            <Label htmlFor="item-component">Component</Label>
            <Select
              value={item.component || ""}
              onValueChange={(v) => update("component", v)}
            >
              <SelectTrigger id="item-component">
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                {componentNames.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Componente React que renderiza o conteúdo da página. A lista contém todos os componentes registrados no sistema.
            </p>
          </div>
        </>
      )}

      {/* Guard */}
      <div className="space-y-1.5">
        <Label htmlFor="item-guard">Guard (proteção de acesso)</Label>
        <Select
          value={item.guard || "protected"}
          onValueChange={(v) => update("guard", v)}
        >
          <SelectTrigger id="item-guard">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GUARD_OPTIONS.map((g) => (
              <SelectItem key={g} value={g}>
                {g}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          <strong>protected</strong> — qualquer usuário logado;{" "}
          <strong>admin</strong> — apenas administradores;{" "}
          <strong>module</strong> — controle granular via <code>permitted_ids</code> (exige Module ID).
        </p>
      </div>

      {/* Module-specific fields */}
      {(item.guard === "module") && (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="item-moduleId">Module ID</Label>
            <Input
              id="item-moduleId"
              value={item.moduleId || ""}
              onChange={(e) => update("moduleId", e.target.value)}
              placeholder="nome_do_modulo"
            />
            <p className="text-xs text-muted-foreground">
              Identificador do módulo no backend. Usado pelo guard <strong>module</strong> para verificar permissão de acesso via <code>require_tab_access</code>.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="item-tabId">Tab ID (opcional)</Label>
            <Input
              id="item-tabId"
              value={item.tabId || ""}
              onChange={(e) => update("tabId", e.target.value)}
              placeholder="nome_da_aba"
            />
            <p className="text-xs text-muted-foreground">
              Identificador da aba interna (opcional). Quando preenchido, a rota aponta para o path do módulo com <code>?tab=tabId</code> e o componente gerencia a aba ativa via URL.
            </p>
          </div>
        </>
      )}

      <Separator />

      {/* Visible in menu */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <Checkbox
            id="item-visible"
            checked={item.visibleInMenu !== false}
            onCheckedChange={(v) => update("visibleInMenu", v !== false)}
          />
          <Label htmlFor="item-visible" className="cursor-pointer">
            Visível no menu lateral
          </Label>
        </div>
        <p className="text-xs text-muted-foreground">
          Se desmarcado, a rota continua existindo e pode ser acessada diretamente pela URL, mas não aparece na barra de navegação lateral.
        </p>
      </div>
    </div>
  );
}

// ─── Preview Tree ──────────────────────────────────────────────
function MenuPreviewTree({ items, depth }) {
  return items.map((item) => (
    <React.Fragment key={item.id || item.label}>
      {item.children ? (
        <div>
          <div
            className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-slate-200/50 dark:hover:bg-slate-800/50"
            style={{ paddingLeft: `${12 + depth * 16}px` }}
          >
            <FolderOpen className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
            <span className="text-xs font-medium truncate">
              {item.label}
            </span>
            <Badge variant="outline" className="text-[10px] px-1 py-0">
              grupo
            </Badge>
          </div>
          {item.children.length > 0 && (
            <MenuPreviewTree items={item.children} depth={depth + 1} />
          )}
        </div>
      ) : (
        <div
          className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-slate-200/50 dark:hover:bg-slate-800/50"
          style={{ paddingLeft: `${12 + depth * 16}px` }}
        >
          <span className="flex-shrink-0 w-4 flex items-center justify-center">
            {renderIconStatic(item.icon) || (
              <FileText className="h-3 w-3 text-muted-foreground" />
            )}
          </span>
          <span className="text-xs truncate">{item.label}</span>
          {item.guard === "admin" && (
            <Badge
              variant="outline"
              className="text-[10px] px-1 py-0 text-red-500 border-red-200"
            >
              admin
            </Badge>
          )}
          {item.visibleInMenu === false && (
            <EyeOff className="h-3 w-3 text-muted-foreground/50" />
          )}
        </div>
      )}
    </React.Fragment>
  ));
}

// ─── Static icon renderer for select previews ──────────────────
function renderIconStatic(name) {
  if (!name || name === "(none)") return null;
  const IconComponent = iconMap[name];
  if (!IconComponent) return null;
  return React.createElement(IconComponent, {
    className: "h-4 w-4 inline-block",
  });
}
